import { defineStore } from "pinia";
import api from "@/services/api";
import { getActiveScenarioId } from "@/components/assignment/gorevAtamaViewStoreBridge";
import { createExcelFromData } from "./assignment/assignmentExcel";
import { buildAssignmentJsonExport, downloadJsonFile, parseAssignmentJsonImport, } from "./assignment/assignmentJson";
import { logger } from "@/utils/logger";
function defaultFrontendChanges() {
    return {
        havuz_gorevleri: [],
        gorev_degisiklikleri: [],
        son_guncelleme: new Date().toISOString(),
    };
}
/**
 * Görev atama modülü state (Pinia).
 * Kaynak: GorevAtamaView.vue setup refs — micro-7-7.
 */
export const useAssignmentStore = defineStore("assignment", {
    state: () => ({
        isRunning: false,
        isExporting: false,
        isSaving: false,
        showParametersModal: false,
        showJsonModal: false,
        showSaveModal: false,
        saveModalResult: null,
        parameters: {
            baslangic: "2025-01-01",
            bitis: "2025-01-31",
            params: {},
            excel: false,
        },
        tarihAtamaSonuclari: null,
        acikGorevOzeti: [],
        izinTakvimi: {},
        uyumsuzlukListesi: [],
        soforIstatistikleri: {},
        genelIstatistikler: {},
        frontendChanges: defaultFrontendChanges(),
        fleetPlanStatus: null,
        fleetPlanStatusLoading: false,
    }),
    getters: {
        hasResults: (state) => state.tarihAtamaSonuclari !== null,
        stats(state) {
            const t = state.tarihAtamaSonuclari;
            const s = t?.ozet_istatistikler;
            if (s) {
                return {
                    toplam_gun: s.toplam_gun || 0,
                    resmi_tatil_gun: s.resmi_tatil_gun || 0,
                    toplam_arac_atama: s.toplam_arac_atama || 0,
                    toplam_sofor_atama: s.toplam_sofor_atama || 0,
                    toplam_uyumsuzluk: state.uyumsuzlukListesi?.length || 0,
                    zimmetli_atama: s.zimmetli_atama || 0,
                    zimmetsiz_atama: s.zimmetsiz_atama || 0,
                    ortalama_mesai: s.ortalama_mesai || 0,
                    kullanilan_arac_sayisi: s.kullanilan_arac_sayisi || 0,
                };
            }
            const gb = (t?.gun_bazli_atamalar || {});
            let toplam_arac = 0, toplam_sofor = 0, toplam_gun = Object.keys(gb).length;
            let resmi_tatil_gun = 0, zimmetli = 0, zimmetsiz = 0, toplam_mesai = 0;
            const kullanilan_araclar = new Set();
            for (const g of Object.values(gb)) {
                if (g?.gun_tipi === "RESMI_TATIL")
                    resmi_tatil_gun += 1;
                const garajlar = g?.garaj_atamalari || {};
                for (const garaj of Object.values(garajlar)) {
                    toplam_arac += garaj?.arac_atamalari?.length || 0;
                    for (const soforAt of garaj?.sofor_atamalari || []) {
                        toplam_sofor += 1;
                        if (soforAt.zimmet_tercihi === "zimmetli") {
                            zimmetli += 1;
                        }
                        else {
                            zimmetsiz += 1;
                        }
                        if (soforAt.ui_sure_dk) {
                            toplam_mesai += Number(soforAt.ui_sure_dk);
                        }
                    }
                    for (const aracAt of garaj?.arac_atamalari || []) {
                        if (aracAt.secilen_arac_id) {
                            kullanilan_araclar.add(aracAt.secilen_arac_id);
                        }
                    }
                }
            }
            const toplam_uy = state.uyumsuzlukListesi?.length || 0;
            const ortalama_mesai = toplam_sofor > 0 ? Math.round(toplam_mesai / toplam_sofor) : 0;
            return {
                toplam_gun,
                resmi_tatil_gun,
                toplam_arac_atama: toplam_arac,
                toplam_sofor_atama: toplam_sofor,
                toplam_uyumsuzluk: toplam_uy,
                zimmetli_atama: zimmetli,
                zimmetsiz_atama: zimmetsiz,
                ortalama_mesai,
                kullanilan_arac_sayisi: kullanilan_araclar.size,
            };
        },
    },
    actions: {
        patchParameters(partial) {
            Object.assign(this.parameters, partial);
        },
        toggleParametersModal() {
            this.showParametersModal = !this.showParametersModal;
        },
        closeParametersModal() {
            this.showParametersModal = false;
        },
        toggleJsonModal() {
            this.showJsonModal = !this.showJsonModal;
        },
        closeJsonModal() {
            this.showJsonModal = false;
        },
        openSaveModal() {
            this.saveModalResult = null;
            this.showSaveModal = true;
        },
        closeSaveModal() {
            this.showSaveModal = false;
            this.saveModalResult = null;
        },
        exportAssignmentJson({ showToast, } = {}) {
            if (!this.tarihAtamaSonuclari) {
                showToast?.("warning", "Önce görev ataması çalıştırın veya JSON yükleyin.", 4000);
                return { success: false };
            }
            const data = buildAssignmentJsonExport({
                parameters: this.parameters,
                tarihAtamaSonuclari: this.tarihAtamaSonuclari,
                izinTakvimi: this.izinTakvimi,
                uyumsuzlukListesi: this.uyumsuzlukListesi,
                soforIstatistikleri: this.soforIstatistikleri,
                genelIstatistikler: this.genelIstatistikler,
                acikGorevOzeti: this.acikGorevOzeti,
                frontendChanges: this.frontendChanges,
            });
            const stamp = new Date().toISOString().slice(0, 10);
            downloadJsonFile(data, `gorev-atama-${stamp}.json`);
            showToast?.("success", "JSON dosyası indirildi.", 3000);
            return { success: true };
        },
        importAssignmentJson(raw, { showToast } = {}) {
            const parsed = parseAssignmentJsonImport(raw);
            if (!parsed.ok) {
                showToast?.("error", parsed.error, 5000);
                return { success: false, error: parsed.error };
            }
            this.applyRunResults({ success: true, sonuclar: parsed.data.sonuclar });
            if (parsed.data.parameters && typeof parsed.data.parameters === "object") {
                this.patchParameters(parsed.data.parameters);
            }
            if (parsed.data.frontendChanges) {
                this.frontendChanges = {
                    ...defaultFrontendChanges(),
                    ...parsed.data.frontendChanges,
                };
            }
            this.showJsonModal = false;
            showToast?.("success", "JSON yüklendi. Ekran güncellendi (DB yazılmadı).", 4000);
            return { success: true };
        },
        resetParameters() {
            this.parameters.params = {};
        },
        applyRunResults(res) {
            if (res?.sonuclar?.izin_takvimi) {
                this.izinTakvimi = res.sonuclar.izin_takvimi;
            }
            else {
                this.izinTakvimi = {};
            }
            if (res?.sonuclar?.uyumsuzluk_listesi) {
                this.uyumsuzlukListesi = res.sonuclar.uyumsuzluk_listesi;
            }
            else {
                this.uyumsuzlukListesi = [];
            }
            if (res?.sonuclar?.sofor_istatistikleri) {
                this.soforIstatistikleri = res.sonuclar.sofor_istatistikleri;
            }
            else {
                this.soforIstatistikleri = {};
            }
            if (res?.sonuclar?.genel_istatistikler) {
                this.genelIstatistikler = res.sonuclar.genel_istatistikler;
            }
            else {
                this.genelIstatistikler = {};
            }
            if (Array.isArray(res?.sonuclar?.acik_gorev_ozeti)) {
                this.acikGorevOzeti = res.sonuclar.acik_gorev_ozeti;
            }
            else {
                this.acikGorevOzeti = [];
            }
            this.tarihAtamaSonuclari =
                res?.sonuclar?.atama_sonuclari?.tarih_atama_sonuclari || null;
        },
        async refreshFleetPlanStatus({ showToast, } = {}) {
            const aktifSenaryoId = getActiveScenarioId();
            if (!aktifSenaryoId) {
                this.fleetPlanStatus = null;
                return null;
            }
            try {
                this.fleetPlanStatusLoading = true;
                const res = await api.getGorevAtamaPlanDurumu(aktifSenaryoId);
                if (res?.success) {
                    this.fleetPlanStatus = res;
                    return res;
                }
                this.fleetPlanStatus = null;
                return null;
            }
            catch (e) {
                console.error("Filo plan durumu alınamadı:", e);
                this.fleetPlanStatus = null;
                showToast?.("warning", "Filo plan durumu kontrol edilemedi.", 4000);
                return null;
            }
            finally {
                this.fleetPlanStatusLoading = false;
            }
        },
        async runAssignment({ showToast, } = {}) {
            const aktifSenaryoId = getActiveScenarioId();
            if (!aktifSenaryoId) {
                showToast?.("warning", "Devam etmek için dönem ve senaryo seçin.", 4000);
                return;
            }
            const planDurumu = await this.refreshFleetPlanStatus({ showToast });
            if (planDurumu && planDurumu.calistirilabilir === false) {
                showToast?.("warning", "Bu senaryoda kayıtlı filo planı yok. Önce Filo Optimizasyonu'nda entegre optimizasyon yapıp planı kaydedin.", 6000);
                return;
            }
            if (planDurumu &&
                typeof planDurumu.zorunlu_hazir_sayisi === "number" &&
                planDurumu.zorunlu_hazir_sayisi < 3) {
                showToast?.("warning", `Filo planı eksik: ${planDurumu.zorunlu_hazir_sayisi}/3 zorunlu gün tipi (Haftaiçi, Cumartesi, Pazar) hazır. Eksik günler atlanabilir.`, 6000);
            }
            try {
                this.isRunning = true;
                const payload = {
                    baslangic: this.parameters.baslangic,
                    bitis: this.parameters.bitis,
                    params: this.parameters.params,
                    excel: false,
                    senaryo_id: aktifSenaryoId,
                };
                const res = await api.runGorevAtama(payload);
                logger.debug("Görev Atama API yanıtı:", res);
                if (!res?.success) {
                    showToast?.("error", res?.error || "Görev ataması başarısız oldu.", 5000);
                    return;
                }
                this.applyRunResults(res);
                const ozet = this.tarihAtamaSonuclari?.ozet_istatistikler;
                const soforAtama = ozet?.toplam_sofor_atama ?? 0;
                const aracAtama = ozet?.toplam_arac_atama ?? 0;
                if (soforAtama === 0 && aracAtama === 0) {
                    showToast?.("warning", "Atama tamamlandı ancak hiç şoför/araç ataması üretilmedi. Senaryo ve filo planını kontrol edin.", 6000);
                }
            }
            catch (e) {
                console.error(e);
                const msg = e?.response?.data?.error ||
                    (e instanceof Error ? e.message : "Görev ataması sırasında hata oluştu.");
                showToast?.("error", msg, 5000);
            }
            finally {
                this.isRunning = false;
            }
        },
        runFromModal({ showToast } = {}) {
            this.showParametersModal = false;
            return this.runAssignment({ showToast });
        },
        async saveToDatabase({ showToast, } = {}) {
            const aktifSenaryoId = getActiveScenarioId();
            if (!aktifSenaryoId) {
                const error = "Kaydetmek için önce bir senaryo seçin.";
                this.saveModalResult = { ok: false, message: error };
                showToast?.("warning", error, 4000);
                return { success: false, error: "senaryo_id yok" };
            }
            if (!this.tarihAtamaSonuclari) {
                const error = "Kaydedilecek sonuç yok — önce görev ataması çalıştırın.";
                this.saveModalResult = { ok: false, message: error };
                showToast?.("warning", error, 4000);
                return { success: false, error: "Kaydedilecek sonuç yok" };
            }
            try {
                this.isSaving = true;
                const res = await api.saveGorevAtamaSonucu({
                    senaryo_id: aktifSenaryoId,
                    baslangic_tarihi: this.parameters.baslangic || null,
                    bitis_tarihi: this.parameters.bitis || null,
                });
                if (res?.success) {
                    const message = `Görev atama sonuçları veritabanına kaydedildi (${res.islem || "tamam"}).`;
                    this.saveModalResult = { ok: true, message };
                    showToast?.("success", message, 4000);
                    return { success: true, islem: res.islem };
                }
                const fail = res?.error || "Kaydetme başarısız.";
                this.saveModalResult = { ok: false, message: fail };
                showToast?.("error", fail, 4000);
                return { success: false, error: fail };
            }
            catch (e) {
                console.error("DB kayıt hatası:", e);
                const fail = "Veritabanına kaydederken hata oluştu.";
                this.saveModalResult = {
                    ok: false,
                    message: e instanceof Error ? e.message : fail,
                };
                showToast?.("error", fail, 4000);
                return {
                    success: false,
                    error: e instanceof Error ? e.message : String(e),
                };
            }
            finally {
                this.isSaving = false;
            }
        },
        async exportToExcel({ showToast, } = {}) {
            try {
                this.isExporting = true;
                if (!this.tarihAtamaSonuclari) {
                    showToast?.("warning", "Önce görev ataması yapmanız gerekiyor", 3000);
                    return { success: false, error: "Atama sonucu yok" };
                }
                const exportData = {
                    atama_sonuclari: this.tarihAtamaSonuclari,
                    izin_takvimi: this.izinTakvimi,
                    uyumsuzluk_listesi: this.uyumsuzlukListesi,
                    sofor_istatistikleri: this.soforIstatistikleri,
                    genel_istatistikler: this.genelIstatistikler,
                    frontend_changes: this.frontendChanges,
                };
                const result = await createExcelFromData(exportData, this.parameters);
                if (result.success) {
                    showToast?.("success", "Excel dosyası başarıyla oluşturuldu ve indirildi!", 5000);
                    return { success: true };
                }
                throw new Error("Excel oluşturma başarısız");
            }
            catch (e) {
                console.error("❌ Excel export hatası:", e);
                showToast?.("error", "Excel export sırasında hata oluştu", 3000);
                return {
                    success: false,
                    error: e instanceof Error ? e.message : String(e),
                };
            }
            finally {
                this.isExporting = false;
            }
        },
        onFrontendChanges(changes) {
            if (changes.havuz_gorevleri) {
                this.frontendChanges.havuz_gorevleri = changes.havuz_gorevleri;
            }
            if (changes.gorev_degisiklikleri) {
                this.frontendChanges.gorev_degisiklikleri =
                    changes.gorev_degisiklikleri;
            }
            this.frontendChanges.son_guncelleme = new Date().toISOString();
        },
    },
});
